import { useTranslation } from '@blocs.i18n';
import { FiltersNameEnum } from '../../../shared/types';
import { FilterDrawerType } from '../types';

export type CommmonFilterLabel = Partial<{
  [K in FiltersNameEnum]: string;
}>;

enum FilterLabel {
  Common = 'common',
}

const useCommonFilterLabel = (): CommmonFilterLabel => {
  const { t } = useTranslation();

  return {
    [FiltersNameEnum.autofillByRoleCriteria]: t('common:filtersName.autofillByRoleCriteria'),
    // Basic filters
    [FiltersNameEnum.stageName]: t('common:form.title.stageName'),
    [FiltersNameEnum.gender]: t('common:filtersName.gender'),
    [FiltersNameEnum.additionallyIdentifiesAs]: t('common:filtersName.additionallyIdentifiesAs'),
    [FiltersNameEnum.ethnicAppearance]: t('common:form.title.ethnicAppearance'),
    [FiltersNameEnum.playableAgeRange]: t('common:form.title.playableAgeRange'),
    [FiltersNameEnum.workingLocation]: t('common:filtersName.workingLocation'),
    [FiltersNameEnum.primaryWorkingLocation]: t('common:filtersName.primaryWorkingLocation'),
    [FiltersNameEnum.commentedBy]: t('common:filtersName.comments'),
    [FiltersNameEnum.pickedBy]: t('common:filtersName.picks'),
    [FiltersNameEnum.role]: t('common:filtersName.roles'),
    [FiltersNameEnum.folder]: t('common:filtersName.folders'),
    // Unions And Reps
    [FiltersNameEnum.unionStatus]: t('common:form.title.unionStatus'),
    [FiltersNameEnum.excludeUnionStatus]: t('common:filtersName.excludeUnionStatus'),
    // Appearances
    [FiltersNameEnum.heightRange]: t('common:form.label.height'),
    [FiltersNameEnum.weightRange]: t('common:form.label.weight'),
    [FiltersNameEnum.hairColor]: t('common:filtersName.hairColor'),
    [FiltersNameEnum.willingToHairColor]: t('common:filtersName.willingToHairColor'),
    [FiltersNameEnum.hairStyle]: t('common:form.title.hairLengthStyle'),
    [FiltersNameEnum.willingToHairStyle]: t('common:filtersName.willingToHairStyle'),
    [FiltersNameEnum.facialHair]: t('common:filtersName.facialHair'),
    [FiltersNameEnum.willingToFacialHair]: t('common:filtersName.willingToFacialHair'),
    [FiltersNameEnum.eyeColor]: t('common:form.title.eyeColor'),
    [FiltersNameEnum.tattoosList]: t('common:form.title.tattoos'),
    [FiltersNameEnum.tattoosNote]: t('common:profileEdit.tattoos.descriptions'),
    [FiltersNameEnum.piercingsList]: t('common:form.title.piercings'),
    [FiltersNameEnum.piercingsNote]: t('common:profileEdit.piercings.descriptions'),
    [FiltersNameEnum.uniqueAttributes]: t('common:filtersName.uniqueAttributes'),
    [FiltersNameEnum.multipleBirth]: t('common:form.title.multipleBirths'),
    [FiltersNameEnum.otherExperience]: t('common:form.title.otherExperience'),
    [FiltersNameEnum.pregnancyDueDateRange]: t('common:form.title.pregnancyDueDate'),
    // Role Preferences
    [FiltersNameEnum.rolePreferences]: t('common:profile.nav.rolePreferences'),
    // Clothing sizes
    [FiltersNameEnum.sizesCategory]: t('common:filtersName.sizesCategory'),
    [FiltersNameEnum.sizesChildrenCategory]: t('common:form.title.sizesChildrenCategory'),
    [FiltersNameEnum.chest]: t('common:profile.sizes.chest'),
    [FiltersNameEnum.neck]: t('common:profile.sizes.neck'),
    [FiltersNameEnum.sleeve]: t('common:profile.sizes.sleeve'),
    [FiltersNameEnum.coat]: t('common:profile.sizes.coat'),
    [FiltersNameEnum.waist]: t('common:filtersName.waist'),
    [FiltersNameEnum.inseam]: t('common:filtersName.inseam'),
    [FiltersNameEnum.glove]: t('common:profile.sizes.glove'),
    [FiltersNameEnum.hat]: t('common:profile.sizes.hat'),
    [FiltersNameEnum.menTShirt]: t('common:profile.sizes.tShirt'),
    [FiltersNameEnum.menShoes]: t('common:profile.sizes.shoe'),
    [FiltersNameEnum.menShoeWidth]: t('common:profile.sizes.shoeWidth'),
    [FiltersNameEnum.dress]: t('common:profile.sizes.dress'),
    [FiltersNameEnum.bust]: t('common:profile.sizes.bust'),
    [FiltersNameEnum.underbust]: t('common:profile.sizes.underbust'),
    [FiltersNameEnum.cup]: t('common:profile.sizes.cup'),
    [FiltersNameEnum.hip]: t('common:profile.sizes.hip'),
    [FiltersNameEnum.womenTShirt]: t('common:profile.sizes.tShirt'),
    [FiltersNameEnum.womenPants]: t('common:profile.sizes.pants'),
    [FiltersNameEnum.womenShoes]: t('common:profile.sizes.shoe'),
    [FiltersNameEnum.womenShoeWidth]: t('common:profile.sizes.shoeWidth'),
    [FiltersNameEnum.childrenShoes]: t('common:profile.sizes.shoe'),
    [FiltersNameEnum.childrenShoeWidth]: t('common:profile.sizes.shoeWidth'),
    [FiltersNameEnum.boys]: t('common:profile.sizes.boys'),
    [FiltersNameEnum.girls]: t('common:profile.sizes.girls'),
    [FiltersNameEnum.toddlers]: t('common:profile.sizes.toddlers'),
    [FiltersNameEnum.infants]: t('common:filtersName.infants'),
    // Skills And Assets
    [FiltersNameEnum.skills]: t('common:filtersName.skills'),
    [FiltersNameEnum.skillLevels]: t('common:filtersName.skillLevels'),
    [FiltersNameEnum.skillOperator]: t('common:filtersName.skillOperator'),
    [FiltersNameEnum.dogBreed]: t('common:filtersName.dogBreed'),
    [FiltersNameEnum.dogBreedSize]: t('common:filtersName.dogBreedSize'),
    [FiltersNameEnum.otherAnimals]: t('common:filtersName.otherAnimals'),
    [FiltersNameEnum.props]: t('common:profile.assets.props'),
    [FiltersNameEnum.sportsEquipment]: t('common:profile.assets.sportsEquipment'),
    [FiltersNameEnum.wardrobe]: t('common:label.assets.wardrobe'),
    [FiltersNameEnum.profession]: t('common:filtersName.profession'),
    [FiltersNameEnum.vehicleType]: t('common:filtersName.vehicleType'),
    [FiltersNameEnum.vehicleYearRange]: t('common:filtersName.vehicleYearRange'),
    [FiltersNameEnum.vehicleMake]: t('common:filtersName.vehicleMake'),
    [FiltersNameEnum.vehicleModel]: t('common:filtersName.vehicleModel'),
    // Documents
    [FiltersNameEnum.documents]: t('common:form.title.documents'),
  };
};

export const useCastingFiltersLabel = (): CommmonFilterLabel => {
  const { t } = useTranslation();

  return {
    ...useCommonFilterLabel(),
    // Basic filters
    [FiltersNameEnum.minorsAgeRange]: t('common:filtersName.minorsAgeRange'),
    // Unions And Reps
    [FiltersNameEnum.unrepresented]: t('common:filtersName.unrepresented'),
    [FiltersNameEnum.represented]: t('common:filtersName.represented'),
    [FiltersNameEnum.talentType]: t('common:filtersName.talentType'),
    [FiltersNameEnum.representatives]: t('common:filtersName.representatives'),
    [FiltersNameEnum.representativeList]: t('common:filtersName.representativeList'),
    // Custom
    [FiltersNameEnum.isNewTalent]: t('common:filtersName.isNewTalent'),
  };
};

export const useRepsFiltersLabel = (): CommmonFilterLabel => {
  const { t } = useTranslation();

  return {
    ...useCommonFilterLabel(),
    // Basic filters
    [FiltersNameEnum.profileStatus]: t('common:filtersName.profileStatus'),
    [FiltersNameEnum.realAgeRange]: t('common:filtersName.realAgeRange'),
    // Exclude Talent With
    [FiltersNameEnum.excludeConflicts]: t('common:filtersName.excludeConflicts'),
    [FiltersNameEnum.excludeBookedOutDates]: t('common:filtersName.excludeBookedOutDates'),
    // Documents
    [FiltersNameEnum.profileListIds]: t('common:filtersName.profileListIds'),
    [FiltersNameEnum.workPermitExpiration]: t('common:filtersName.workPermitExpiration'),
  };
};

export const useTalentScoutFiltersLabel = (): CommmonFilterLabel => {
  const { t } = useTranslation();

  return {
    ...useCommonFilterLabel(),
    [FiltersNameEnum.seekingRepresentation]: t('common:filtersName.seekingRepresentation'),
  };
};

export const useFiltersLabel = (type: FilterDrawerType | FilterLabel = FilterLabel.Common): CommmonFilterLabel => {
  return {
    [FilterDrawerType.Casting]: useCastingFiltersLabel(),
    [FilterDrawerType.Reps]: useRepsFiltersLabel(),
    [FilterDrawerType.TalentScout]: useTalentScoutFiltersLabel(),
    [FilterLabel.Common]: useCommonFilterLabel(),
  }[type];
};
